import { useCallback, useEffect, useRef, useState } from 'react';

import { useServiceFeatures } from 'hooks/useServiceContext';
import { useToast } from 'hooks/useToaster';
import { getThreeGCommodities } from 'lib/api/getThreeGCommodities';
import { TMSCommodity } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';

import { useQuickQuoteContext } from '../../../context/QuickQuoteContext';

export function useThreeGCommodities(): {
  commodities: Maybe<TMSCommodity[]>;
  isLoading: boolean;
  hasCustomer: boolean;
  hasCustomerId: boolean;
} {
  const { toast } = useToast();
  const { ltlCustomer } = useQuickQuoteContext();
  const { ltlTMSIntegrations } = useServiceFeatures();
  const [commodities, setCommodities] = useState<Maybe<TMSCommodity[]>>(null);
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);

  const tmsId = ltlTMSIntegrations?.find((i) => i.name === TMS.ThreeG)?.id;

  const customerID =
    ltlCustomer?.customerDbId ??
    (() => {
      const parsed = parseInt(ltlCustomer?.customerId ?? '', 10);
      return Number.isFinite(parsed) ? parsed : undefined;
    })();

  const customerExternalId = ltlCustomer?.customerId ?? ltlCustomer?.externalId;
  const fallbackExternalId =
    ltlCustomer?.externalId &&
    ltlCustomer?.externalId !== ltlCustomer?.customerId
      ? ltlCustomer?.externalId
      : undefined;

  const fetchCommodities = useCallback(async () => {
    if (!tmsId || (!customerID && !customerExternalId)) {
      setCommodities(null);
      return;
    }

    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    try {
      const res = await getThreeGCommodities(tmsId, {
        customerID,
        externalTmsId: customerExternalId,
        forceRefresh: false,
      });

      if (requestId !== requestIdRef.current) {
        return;
      }

      if (res.isOk()) {
        let commodityList = res.value.commodityList ?? [];
        if (!customerID && commodityList.length === 0 && fallbackExternalId) {
          const fallbackRes = await getThreeGCommodities(tmsId, {
            customerID,
            externalTmsId: fallbackExternalId,
            forceRefresh: false,
          });

          if (requestId !== requestIdRef.current) {
            return;
          }

          if (fallbackRes.isOk()) {
            commodityList = fallbackRes.value.commodityList ?? [];
          } else {
            toast({
              description:
                fallbackRes.error.message ?? 'Failed to load commodities',
              variant: 'destructive',
            });
            setCommodities([]);
            return;
          }
        }

        setCommodities(commodityList);
        return;
      }

      toast({
        description: res.error.message ?? 'Failed to load commodities',
        variant: 'destructive',
      });
      setCommodities([]);
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [tmsId, customerID, customerExternalId, fallbackExternalId, toast]);

  useEffect(() => {
    if (!tmsId || (!customerID && !customerExternalId)) {
      setCommodities(null);
      return;
    }
    fetchCommodities();
  }, [tmsId, customerID, customerExternalId, fetchCommodities]);

  return {
    commodities,
    isLoading,
    hasCustomer: Boolean(ltlCustomer),
    hasCustomerId: customerID != null,
  };
}
